import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import QuestionForm from '../../components/admin/QuestionForm';
import { getExam, listQuestions, addQuestion, updateQuestion, deleteQuestion, togglePublish, getAllCandidates, getExamCandidates, assignCandidates, updateExam, getBankQuestions, getExamBankQuestions, addBankQuestionToExam, removeBankQuestionFromExam } from '../../api/admin.api';

const MetaPill = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-medium">
    {icon} {label}
  </span>
);

const ExamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('questions');

  const load = useCallback(async () => {
    const [eRes, qRes] = await Promise.all([getExam(id), listQuestions(id)]);
    setExam(eRes.data.data);
    setQuestions(qRes.data.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAddQuestion = async (formData) => {
    await addQuestion(id, formData);
    setModalOpen(false);
    load();
  };

  const handleEditQuestion = async (formData) => {
    await updateQuestion(id, editQuestion.id, formData);
    setEditQuestion(null);
    load();
  };

  const handleDelete = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    await deleteQuestion(id, qid);
    load();
  };

  const handleTogglePublish = async () => {
    await togglePublish(id);
    load();
  };

  if (loading) return <AdminLayout><Spinner className="py-20" /></AdminLayout>;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => navigate('/admin/exams')}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-slate-400 text-sm">Exams</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-600 text-sm font-medium truncate max-w-xs">{exam.title}</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{exam.title}</h1>
            {exam.description && <p className="text-slate-500 text-sm mt-1">{exam.description}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <MetaPill icon="⏱" label={`${exam.duration_minutes} min`} />
              <MetaPill icon="📊" label={`${exam.total_marks} marks`} />
              <MetaPill icon="❓" label={`${questions.length} questions`} />
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                exam.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${exam.is_published ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                {exam.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/exams/${id}/edit`)}>
              Edit
            </Button>
            <Button
              variant={exam.is_published ? 'secondary' : 'success'}
              size="sm"
              onClick={handleTogglePublish}
            >
              {exam.is_published ? 'Unpublish' : 'Publish'}
            </Button>
            <Link to={`/admin/exams/${id}/results`}>
              <Button variant="ghost" size="sm">Results</Button>
            </Link>
            <Link to={`/admin/exams/${id}/grading`}>
              <Button variant="ghost" size="sm">Grade</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit mb-6">
        {[
          { id: 'questions', label: 'Questions' },
          { id: 'bank', label: 'Question Bank' },
          { id: 'upload', label: 'Bulk Upload' },
          { id: 'candidates', label: 'Candidates' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
              activeTab === tab.id
                ? 'bg-white text-primary-600 shadow-card'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'questions' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">{questions.length} question{questions.length !== 1 ? 's' : ''}</p>
            <Button onClick={() => setModalOpen(true)}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Question
            </Button>
          </div>

          {questions.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-3">❓</div>
              <h3 className="text-slate-700 font-semibold mb-1">No questions yet</h3>
              <p className="text-slate-400 text-sm mb-4">Add questions manually or use bulk upload.</p>
              <Button onClick={() => setModalOpen(true)}>Add First Question</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={q.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-card hover:shadow-card-md transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-xs text-slate-400 font-medium">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                        {q.section && (
                          <span className="text-xs bg-violet-100 text-violet-700 font-medium px-2 py-0.5 rounded-full">
                            {q.section}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-800 font-medium leading-snug">{q.question_text}</p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {q.options.filter(Boolean).map((opt) => (
                          <div
                            key={opt.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border ${
                              opt.is_correct
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800 font-medium'
                                : 'bg-slate-50 border-slate-200 text-slate-600'
                            }`}
                          >
                            {opt.is_correct ? (
                              <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <span className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
                            )}
                            {opt.option_text}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => setEditQuestion(q)}>Edit</Button>
                      <Button size="sm" variant="danger" onClick={() => handleDelete(q.id)}>Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'bank' && (
        <BankQuestionsTab examId={id} onDone={load} />
      )}

      {activeTab === 'upload' && (
        <BulkUploadTab examId={id} onDone={load} />
      )}

      {activeTab === 'candidates' && (
        <CandidatesTab examId={id} exam={exam} onExamUpdate={load} />
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add Question" size="lg">
        <QuestionForm onSave={handleAddQuestion} onCancel={() => setModalOpen(false)} />
      </Modal>

      <Modal isOpen={!!editQuestion} onClose={() => setEditQuestion(null)} title="Edit Question" size="lg">
        {editQuestion && (
          <QuestionForm
            initial={{ ...editQuestion, options: editQuestion.options.filter(Boolean) }}
            onSave={handleEditQuestion}
            onCancel={() => setEditQuestion(null)}
          />
        )}
      </Modal>
    </AdminLayout>
  );
};

const CandidatesTab = ({ examId, exam, onExamUpdate }) => {
  const [allCandidates, setAllCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [restricted, setRestricted] = useState(exam.restrict_to_assigned || false);
  const [search, setSearch] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('all'); // 'all' | 'assigned' | 'unassigned'
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([getAllCandidates(), getExamCandidates(examId)]).then(([allRes, assignedRes]) => {
      setAllCandidates(allRes.data.data);
      setSelected(new Set(assignedRes.data.data.map((c) => c.id)));
    });
  }, [examId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map((c) => c.id)));
  const clearAll = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await assignCandidates(examId, [...selected]);
      await updateExam(examId, { restrict_to_assigned: restricted });
      setMsg('Saved successfully.');
      onExamUpdate();
    } catch {
      setMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const q = search.toLowerCase();
  const filtered = allCandidates.filter((c) => {
    const matchSearch = !q ||
      (c.full_name || '').toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.mobile || '').includes(q) ||
      (c.college_name || '').toLowerCase().includes(q);
    const isAssigned = selected.has(c.id);
    const matchFilter = filterAssigned === 'all' ||
      (filterAssigned === 'assigned' ? isAssigned : !isAssigned);
    return matchSearch && matchFilter;
  });

  return (
    <div className="max-w-3xl space-y-5">
      {/* Restrict toggle */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-card">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-slate-800">Restrict to assigned candidates only</p>
          <div
            onClick={() => setRestricted((v) => !v)}
            className={`relative w-10 h-6 rounded-full cursor-pointer transition-colors duration-200 ${restricted ? 'bg-primary-600' : 'bg-slate-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${restricted ? 'translate-x-4' : ''}`} />
          </div>
        </div>
        <p className="text-xs text-slate-400">When enabled, only selected candidates below can access this exam.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
        {/* Search + filter bar */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, mobile, college…"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={filterAssigned}
            onChange={(e) => setFilterAssigned(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            <option value="all">All</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <button onClick={selectAll} className="text-xs text-primary-600 hover:underline whitespace-nowrap">Select All</button>
          <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-600 hover:underline whitespace-nowrap">Clear</button>
        </div>

        {/* Candidate list as table */}
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10">No candidates found.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                <tr>
                  <th className="px-4 py-2.5 w-10" />
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">College</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    className={`cursor-pointer transition-colors ${selected.has(c.id) ? 'bg-primary-50 hover:bg-primary-100' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{c.full_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{c.email}</td>
                    <td className="px-4 py-3 text-slate-500">{c.mobile || '—'}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{c.college_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            <span className="font-semibold text-slate-700">{selected.size}</span> candidate{selected.size !== 1 ? 's' : ''} selected
            {search || filterAssigned !== 'all' ? ` · showing ${filtered.length} of ${allCandidates.length}` : ` · ${allCandidates.length} total`}
          </p>
          <div className="flex items-center gap-3">
            {msg && <span className="text-xs text-emerald-600">{msg}</span>}
            <Button onClick={handleSave} loading={saving} size="sm">Save Assignments</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BankQuestionsTab = ({ examId, onDone }) => {
  const [bankQuestions, setBankQuestions] = useState([]);
  const [examBankQuestions, setExamBankQuestions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bkRes, ebqRes] = await Promise.all([
        getBankQuestions({ search, limit: 100 }),
        getExamBankQuestions(examId),
      ]);
      setBankQuestions(bkRes.data.rows);
      setExamBankQuestions(ebqRes.data.data);
    } finally {
      setLoading(false);
    }
  }, [examId, search]);

  useEffect(() => { load(); }, [load]);

  const linkedIds = new Set(examBankQuestions.map((q) => q.id));

  const handleAdd = async (bqId) => {
    setAdding(bqId);
    try {
      await addBankQuestionToExam(examId, bqId);
      await load();
      onDone();
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (bqId) => {
    setRemoving(bqId);
    try {
      await removeBankQuestionFromExam(examId, bqId);
      await load();
      onDone();
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-5">
      {examBankQuestions.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Added from Bank ({examBankQuestions.length})</p>
          <div className="space-y-2">
            {examBankQuestions.map((q) => (
              <div key={q.id} className="bg-white rounded-xl border border-primary-200 px-4 py-3 flex items-center justify-between gap-4 shadow-card">
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-semibold mr-2">From Bank</span>
                  <span className="text-sm text-slate-700">{q.question_text}</span>
                </div>
                <button
                  onClick={() => handleRemove(q.id)}
                  disabled={removing === q.id}
                  className="text-xs text-red-500 hover:underline flex-shrink-0 disabled:opacity-50"
                >
                  {removing === q.id ? '…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Add from Question Bank</p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bank questions…"
            className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400"
          />
        </div>
        {loading ? <Spinner className="py-10" /> : (
          bankQuestions.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No bank questions found. Create some in the <Link to="/admin/question-bank" className="text-primary-600 hover:underline">Question Bank</Link>.</p>
          ) : (
            <div className="space-y-2">
              {bankQuestions.map((q) => {
                const isLinked = linkedIds.has(q.id);
                return (
                  <div key={q.id} className={`bg-white rounded-xl border px-4 py-3 flex items-center justify-between gap-4 ${isLinked ? 'border-emerald-200 opacity-60' : 'border-slate-200'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 line-clamp-1">{q.question_text}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{q.marks} mark(s){q.tags?.length ? ` · ${q.tags.join(', ')}` : ''}</p>
                    </div>
                    {isLinked ? (
                      <span className="text-xs text-emerald-600 font-medium flex-shrink-0">Added</span>
                    ) : (
                      <button
                        onClick={() => handleAdd(q.id)}
                        disabled={adding === q.id}
                        className="text-xs text-primary-600 hover:underline flex-shrink-0 disabled:opacity-50"
                      >
                        {adding === q.id ? '…' : '+ Add'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const BulkUploadTab = ({ examId, onDone }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { bulkUpload } = require('../../api/admin.api');
  const axiosInstance = require('../../api/axiosInstance').default;

  const downloadSample = async () => {
    const res = await axiosInstance.get('/admin/exams/questions/sample', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_exam_questions.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await bulkUpload(examId, file);
      setResult(res.data);
      onDone();
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Format guide + Download sample */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-800 mb-2">Supported Question Types & Columns</p>
            <table className="text-xs text-blue-700 w-full mb-2">
              <thead>
                <tr className="border-b border-blue-200">
                  <th className="text-left pb-1 font-semibold">Type</th>
                  <th className="text-left pb-1 font-semibold">Required Columns</th>
                  <th className="text-left pb-1 font-semibold">Optional</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                <tr>
                  <td className="py-1 font-mono">mcq</td>
                  <td className="py-1">question_text, question_type, option_a, option_b, correct_option (A/B/C/D)</td>
                  <td className="py-1">option_c, option_d, marks, explanation</td>
                </tr>
                <tr>
                  <td className="py-1 font-mono">true_false</td>
                  <td className="py-1">question_text, question_type, correct_option (True/False)</td>
                  <td className="py-1">marks, explanation</td>
                </tr>
                <tr>
                  <td className="py-1 font-mono">fill_in_the_blank</td>
                  <td className="py-1">question_text, question_type, correct_answer</td>
                  <td className="py-1">case_sensitive (true/false), marks, explanation</td>
                </tr>
                <tr>
                  <td className="py-1 font-mono">short_answer</td>
                  <td className="py-1">question_text, question_type</td>
                  <td className="py-1">marks, explanation</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-blue-600">Download the sample file to see all 4 question types with examples.</p>
          </div>
          <button
            onClick={downloadSample}
            className="flex-shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Sample
          </button>
        </div>
      </div>

      {/* Drop zone */}
      <label className={`flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-2xl cursor-pointer transition-colors mb-4 ${
        file ? 'border-primary-400 bg-primary-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'
      }`}>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
        />
        {file ? (
          <>
            <svg className="w-8 h-8 text-primary-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm font-medium text-primary-600">{file.name}</p>
            <p className="text-xs text-primary-400 mt-0.5">Click to change file</p>
          </>
        ) : (
          <>
            <svg className="w-8 h-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <p className="text-sm font-medium text-slate-500">Click to select CSV or XLSX file</p>
            <p className="text-xs text-slate-400 mt-0.5">Supports .csv, .xlsx, .xls</p>
          </>
        )}
      </label>

      <Button onClick={handleUpload} loading={uploading} disabled={!file}>
        Upload Questions
      </Button>

      {result && (
        <div className="mt-4 space-y-2">
          {result.inserted > 0 && <Alert type="success">Successfully inserted {result.inserted} question{result.inserted !== 1 ? 's' : ''}.</Alert>}
          {result.errors?.length > 0 && (
            <Alert type="warning">
              {result.errors.length} row(s) had errors:
              <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Alert>
          )}
          {result.message && <Alert type="error">{result.message}</Alert>}
        </div>
      )}
    </div>
  );
};

export default ExamDetail;
